import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Clock, Gauge, CalendarDays, Users } from "lucide-react";
import { PROGRAMS, LINKS } from "../lib/config";
import CalendlyModal from "../components/CalendlyModal";
import Nav from "../components/Nav";

const fadeUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
};

export default function ProgramPage() {
    const { slug } = useParams();
    const program = PROGRAMS.find((p) => p.slug === slug);
    const [calOpen, setCalOpen] = useState(false);
    const onBook = () => setCalOpen(true);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    if (!program) {
        return (
            <div className="min-h-screen bg-powder flex flex-col items-center justify-center gap-6 px-5 text-center">
                <h1 className="font-serif text-4xl">Programma non trovato</h1>
                <Link to="/#programs" className="btn-fuchsia" data-testid="program-back-home">Torna ai programmi</Link>
            </div>
        );
    }

    const specs = [
        { icon: Gauge, label: "Difficoltà", value: program.difficulty },
        { icon: Clock, label: "Durata", value: program.duration },
        { icon: CalendarDays, label: "Frequenza", value: program.frequency },
        { icon: Users, label: "Livello", value: program.level },
    ];

    return (
        <div className="min-h-screen bg-powder text-ink" data-testid="program-page">
            <div className="grain-overlay" />
            <Nav onBook={onBook} variant="solid" />

            {/* Hero */}
            <section className="relative min-h-[70vh] md:min-h-[80vh] flex items-end overflow-hidden bg-ink">
                <motion.img
                    initial={{ scale: 1.12 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1.5, ease: [0.65, 0, 0.35, 1] }}
                    src={program.img}
                    alt={program.title}
                    className="absolute inset-0 w-full h-full object-cover opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-transparent" />

                <div className="relative z-10 px-5 md:px-12 pb-12 md:pb-16 max-w-[1400px] mx-auto w-full text-white">
                    <motion.span
                        variants={fadeUp} initial="initial" animate="animate" transition={{ duration: 0.7 }}
                        className="font-serif italic text-fuchsia text-3xl md:text-5xl block mb-3"
                    >
                        {program.num}
                    </motion.span>
                    <motion.h1
                        variants={fadeUp} initial="initial" animate="animate" transition={{ duration: 0.8, delay: 0.1 }}
                        className="font-serif text-4xl md:text-6xl lg:text-7xl font-bold leading-[0.95] max-w-4xl"
                    >
                        {program.title}
                    </motion.h1>
                    <motion.p
                        variants={fadeUp} initial="initial" animate="animate" transition={{ duration: 0.8, delay: 0.2 }}
                        className="font-serif italic text-xl md:text-2xl text-white/90 mt-5"
                    >
                        {program.tagline}
                    </motion.p>
                </div>
            </section>

            {/* Specs bar */}
            <section className="bg-ink text-white">
                <div className="max-w-[1400px] mx-auto grid grid-cols-2 md:grid-cols-4">
                    {specs.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <div key={i} className="p-6 md:p-8 border-b md:border-b-0 md:border-r border-white/10 last:border-r-0">
                                <Icon size={20} className="text-fuchsia mb-3" />
                                <p className="text-[10px] uppercase tracking-[0.28em] text-white/50 mb-1">{s.label}</p>
                                <p className="font-serif text-lg leading-tight">{s.value}</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Body */}
            <section className="max-w-[1400px] mx-auto px-5 md:px-12 py-16 md:py-24">
                <div className="grid md:grid-cols-12 gap-12 md:gap-16">
                    <div className="md:col-span-7">
                        <motion.p
                            variants={fadeUp} initial="initial" whileInView="animate" viewport={{ once: true }}
                            transition={{ duration: 0.7 }}
                            className="font-serif text-2xl md:text-3xl leading-snug text-ink mb-12"
                        >
                            {program.intro}
                        </motion.p>

                        <motion.div
                            variants={fadeUp} initial="initial" whileInView="animate" viewport={{ once: true }}
                            transition={{ duration: 0.7 }}
                        >
                            <h2 className="font-serif text-3xl md:text-4xl mb-6">Cosa include</h2>
                            <ul className="space-y-4 mb-14">
                                {program.includes.map((item, i) => (
                                    <li key={i} className="flex gap-4 items-start border-b border-ink/10 pb-4">
                                        <span className="mt-1 w-6 h-6 rounded-full bg-fuchsia text-white flex items-center justify-center shrink-0">
                                            <Check size={14} />
                                        </span>
                                        <span className="font-sans text-base md:text-lg text-ink">{item}</span>
                                    </li>
                                ))}
                            </ul>

                            <h2 className="font-serif text-3xl md:text-4xl mb-6">I benefici per te</h2>
                            <div className="grid sm:grid-cols-2 gap-4">
                                {program.benefits.map((b, i) => (
                                    <div key={i} className="bg-white p-5 border border-ink/10">
                                        <span className="font-serif italic text-fuchsia text-xl">0{i + 1}</span>
                                        <p className="font-sans text-sm md:text-base mt-2 leading-relaxed">{b}</p>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>

                    {/* Sticky purchase card */}
                    <div className="md:col-span-5">
                        <motion.div
                            variants={fadeUp} initial="initial" whileInView="animate" viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="md:sticky md:top-24 bg-ink text-white p-8 md:p-10"
                        >
                            <p className="text-xs uppercase tracking-[0.28em] text-white/50 mb-4">Iniziamo insieme</p>
                            <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-5">{program.title}</h3>
                            <p className="font-sans text-white/70 leading-relaxed mb-6">{program.forWho}</p>

                            <div className="flex items-center gap-2 text-sm text-white/60 mb-8">
                                <span className="bg-white/10 rounded-full px-3 py-1">In presenza</span>
                                <span className="bg-white/10 rounded-full px-3 py-1">Online</span>
                                <span className="bg-white/10 rounded-full px-3 py-1">Personalizzato</span>
                            </div>

                            <button
                                onClick={onBook}
                                data-testid="program-book-btn"
                                className="btn-fuchsia w-full justify-center text-base"
                            >
                                Prenota una consulenza in presenza o online
                                <ArrowUpRight size={18} />
                            </button>
                            <a
                                href={LINKS.whatsappStart}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="program-start-btn"
                                className="btn-ghost !text-white !border-white/40 w-full justify-center text-base mt-3"
                            >
                                Inizia ora — sono pronto/a
                                <ArrowUpRight size={18} />
                            </a>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Other programs */}
            <section className="bg-white py-16 md:py-24 border-t border-ink/10">
                <div className="max-w-[1400px] mx-auto px-5 md:px-12">
                    <h2 className="font-serif text-3xl md:text-4xl mb-10">Altri programmi</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                        {PROGRAMS.filter((p) => p.slug !== slug).map((p) => (
                            <Link
                                key={p.slug}
                                to={`/programma/${p.slug}`}
                                data-testid={`other-program-${p.slug}`}
                                className="group relative overflow-hidden bg-ink aspect-[3/4]"
                            >
                                <img src={p.img} alt={p.title} className="w-full h-full object-cover opacity-70 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700" />
                                <div className="absolute inset-0 bg-gradient-to-t from-ink to-transparent" />
                                <div className="absolute bottom-0 p-4 text-white">
                                    <span className="font-serif italic text-fuchsia">{p.num}</span>
                                    <p className="font-serif text-base md:text-lg leading-tight mt-1">{p.title}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <CalendlyModal open={calOpen} onOpenChange={setCalOpen} />
        </div>
    );
}
